import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { type RootState } from '@/platform/store/types';
import {
  type IntegrationStatus,
  type WhatsappConnectedData,
  type WhatsappErrorScope,
} from '../models/whatsapp-integration.model';
import type { WithSlice } from '@reduxjs/toolkit';

export interface WhatsappIntegrationError {
  step: WhatsappErrorScope;
  detail: string;
}

export interface WhatsappIntegrationState {
  status: IntegrationStatus;
  data: WhatsappConnectedData | null;
  error: WhatsappIntegrationError | null;
}

const initialState: WhatsappIntegrationState = {
  status: 'idle',
  data: null,
  error: null,
};

export const whatsappIntegrationSlice = createSlice({
  name: 'whatsappIntegration',
  initialState,
  reducers: {
    setStatusLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    setStatusConnecting: (state) => {
      state.status = 'connecting';
      state.error = null;
    },
    setConnected: (state, action: PayloadAction<WhatsappConnectedData>) => {
      state.status = 'connected';
      state.data = action.payload;
      state.error = null;
    },
    setDisconnected: (state) => {
      state.status = 'disconnected';
      state.data = null;
      state.error = null;
    },
    setError: (state, action: PayloadAction<WhatsappIntegrationError>) => {
      state.status = 'error';
      state.error = action.payload;
    },
    resetIntegrationState: () => initialState,
  },
});

export const {
  setStatusLoading,
  setStatusConnecting,
  setConnected,
  setDisconnected,
  setError,
  resetIntegrationState,
} = whatsappIntegrationSlice.actions;

const selectSlice = (state: RootState): WhatsappIntegrationState =>
  state.whatsappIntegration ?? initialState;

export const selectWhatsappIntegrationStatus = (state: RootState) =>
  selectSlice(state).status;

export const selectWhatsappIntegrationData = (state: RootState) =>
  selectSlice(state).data;

export const selectWhatsappIntegrationError = (state: RootState) =>
  selectSlice(state).error;

// Registers this slice in the typed root state without the platform importing it.
declare module '@/platform/store/rootReducer' {
  interface LazyLoadedSlices extends WithSlice<typeof whatsappIntegrationSlice> {}
}
